import { FolderOpen, ShieldAlert } from "lucide-react";
import { useProjectFolders } from "../hooks";
import type { FolderNode } from "../types";

// Wspólna sekcja „udostępnij pliki z projektu ZE" dla KAŻDEJ drogi narodzin zamówienia:
// „Zleć podwykonawcy" z projektu technicznego/kosztorysu/Wód i zatwierdzenie zapotrzebowania.
// Jeden kontrakt z backendem (file_sharing.parse_share_params), więc opcja „wszystkie
// niepoufne" znaczy wszędzie dokładnie to samo — żywą regułę, nie zamrożoną listę folderów.

export interface ProjectFileShareValue {
  enabled: boolean;
  /** true = „wszystkie niepoufne" (reguła żywa); false = tylko zaznaczone foldery. */
  all: boolean;
  slugs: Set<string>;
}

export const emptyProjectFileShare = (): ProjectFileShareValue => ({
  enabled: false, all: true, slugs: new Set(),
});

/** Wybór jest kompletny, gdy nie udostępniamy nic albo wskazano konkretne foldery. */
export const projectFileShareValid = (v: ProjectFileShareValue): boolean =>
  !v.enabled || v.all || v.slugs.size > 0;

/** Pola formularza wysyłane do backendu (te same nazwy w multipart i w JSON). */
export function projectFileShareFields(v: ProjectFileShareValue): Record<string, string> {
  const out: Record<string, string> = { share_project_files: v.enabled ? "true" : "false" };
  if (v.enabled) {
    out.share_all_nonconfidential = v.all ? "true" : "false";
    if (!v.all) out.share_folder_slugs = JSON.stringify([...v.slugs]);
  }
  return out;
}

// Rekurencyjny wiersz wyboru folderu do udostępnienia. Folder poufny jest zablokowany
// (nie podlega udostępnieniu), ale jego dzieci renderujemy dalej — podrzędny folder/plik
// może być jawnie „odpoufniony" i wtedy wolno go wybrać. Zaznaczenie folderu nadrzędnego
// obejmuje poddrzewo (jawną część), więc dzieci pokazujemy jako zaznaczone i zablokowane.
export function ShareFolderRow({
  node, depth, selected, ancestorSelected, onToggle,
}: {
  node: FolderNode; depth: number; selected: Set<string>;
  ancestorSelected: boolean; onToggle: (slug: string) => void;
}) {
  const confidential = node.is_confidential;
  const checked = !confidential && (ancestorSelected || selected.has(node.slug));
  return (
    <>
      <label
        className={`flex items-center gap-1.5 py-0.5 text-xs text-[var(--color-text)] ${
          confidential ? "opacity-60" : "cursor-pointer"
        }`}
        style={{ paddingLeft: `${depth * 16}px` }}
        title={confidential ? "Folder poufny — nie podlega udostępnieniu podwykonawcy." : undefined}
      >
        <input
          type="checkbox"
          className="shrink-0 accent-[var(--color-primary)]"
          checked={checked}
          disabled={confidential || ancestorSelected}
          onChange={() => onToggle(node.slug)}
        />
        <span className="truncate">{node.label}</span>
        {confidential && <ShieldAlert size={11} className="shrink-0 text-amber-600" />}
        {node.file_count > 0 && (
          <span className="shrink-0 rounded bg-[var(--color-bg-muted)] px-1 font-mono text-[10px] text-[var(--color-text-muted)]">
            {node.file_count}
          </span>
        )}
      </label>
      {node.children.map((c) => (
        <ShareFolderRow
          key={c.slug}
          node={c}
          depth={depth + 1}
          selected={selected}
          ancestorSelected={checked}
          onToggle={onToggle}
        />
      ))}
    </>
  );
}

export function ProjectFileShareFields({
  connectionId, value, onChange,
}: {
  connectionId: string;
  value: ProjectFileShareValue;
  onChange: (next: ProjectFileShareValue) => void;
}) {
  // Drzewo ciągniemy dopiero po zaznaczeniu opcji — komplet folderów projektu to niemały
  // payload, a większość zamówień powstaje bez udostępniania plików.
  const foldersQ = useProjectFolders(value.enabled && connectionId ? connectionId : "");
  const toggleSlug = (slug: string) => {
    const slugs = new Set(value.slugs);
    if (slugs.has(slug)) slugs.delete(slug);
    else slugs.add(slug);
    onChange({ ...value, slugs });
  };

  return (
    <div className="space-y-2 rounded border border-[var(--color-border-muted)] bg-[var(--color-bg-elevated)] p-2">
      <label className="flex items-start gap-1.5 text-xs text-[var(--color-text)]">
        <input
          type="checkbox"
          className="mt-0.5 shrink-0"
          checked={value.enabled}
          onChange={(e) => onChange({ ...value, enabled: e.target.checked })}
        />
        <span>
          <span className="inline-flex items-center gap-1.5">
            <FolderOpen size={13} className="text-[var(--color-primary)]" />
            Udostępnij pliki z projektu ZE (zakładka „Pliki”)
          </span>
          <span className="block text-[11px] leading-snug text-[var(--color-text-muted)]">
            Podwykonawca i zlecający zobaczą w zamówieniu sekcję „Pliki z projektu ZE” — żywy
            podgląd wybranych folderów (bez kopii). Foldery poufne nigdy nie są udostępniane.
          </span>
        </span>
      </label>
      {value.enabled && (
        <div className="space-y-1.5 pl-5">
          <label className="flex items-center gap-1.5 text-xs text-[var(--color-text)]">
            <input
              type="radio"
              name="share-folders-mode"
              checked={value.all}
              onChange={() => onChange({ ...value, all: true })}
            />
            Wszystkie niepoufne foldery
          </label>
          {value.all && (
            <p className="text-[11px] leading-snug text-[var(--color-text-muted)]">
              Reguła działa w przód: pliki dołożone później też trafią do tego zamówienia.
            </p>
          )}
          <label className="flex items-center gap-1.5 text-xs text-[var(--color-text)]">
            <input
              type="radio"
              name="share-folders-mode"
              checked={!value.all}
              onChange={() => onChange({ ...value, all: false })}
            />
            Tylko wybrane foldery
          </label>
          {!value.all && (
            foldersQ.isLoading ? (
              <p className="text-[11px] text-[var(--color-text-muted)]">Ładowanie folderów…</p>
            ) : (foldersQ.data?.folders ?? []).length === 0 ? (
              // Pusto zwykle znaczy „brak dostępu do workspace'u ZE" — wtedy zostaje opcja
              // „wszystkie niepoufne", którą backend rozstrzyga po uprawnieniach, nie po drzewie.
              <p className="text-[11px] text-amber-600">
                Nie widzisz folderów tego projektu — wybierz „wszystkie niepoufne”.
              </p>
            ) : (
              <div className="max-h-56 space-y-0.5 overflow-y-auto rounded border border-[var(--color-border-muted)] bg-[var(--color-bg-surface)] p-2">
                {(foldersQ.data?.folders ?? []).map((n) => (
                  <ShareFolderRow
                    key={n.slug}
                    node={n}
                    depth={0}
                    selected={value.slugs}
                    ancestorSelected={false}
                    onToggle={toggleSlug}
                  />
                ))}
              </div>
            )
          )}
          {!value.all && value.slugs.size === 0 && (
            <p className="text-[11px] text-amber-600">
              Wskaż co najmniej jeden folder albo wybierz „wszystkie niepoufne”.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
